import {
    BehavioralFactor,
    behavioralFactorLabelList
} from '../../core-data-model/BehavioralFactor.model';
import { BehavioralFactorRanges } from '../../core-data-model/BehavioralFactorRanges.model';
import {
    MinMax as MinMaxEntry,
    MinMax
} from '../../core-data-model/Common.model';
import {
    FactorEntry,
    config,
    DragStartEndMap,
    DraggableOnMousedown
} from './BehavioralRanges.interface';

export class BehavioralRangesUtil {
    static factors: FactorEntry[] = behavioralFactorLabelList
        .filter((entry) => entry.key !== 'E')
        .map((entry, index) =>
            Object.assign({}, entry, {
                height: 60 + 40 * index
            })
        );

    /**
     * Check whether the given input ranges are valid.
     */
    static validInputRanges(ranges: BehavioralFactorRanges): boolean {
        return ranges.A.min !== Number.POSITIVE_INFINITY;
    }

    /**
     * Scale the given sigma value [-3,3] to the [0,6] scale.
     *
     * @param value
     */
    private static scaledValue(value: number): number {
        return (value + 3) / 6;
    }
    /**
     * Scale the given [0,6] scale number to its sigma value [-3, 3]
     *
     * @param scaled
     */
    private static inverseScaledValue(scaled: number): number {
        return scaled * 6 - 3;
    }
    /**
     * Get a range rect's x value from a minMax map.
     *
     * @param minMax
     */
    private static getRectX(minMax: MinMaxEntry): number {
        return (
            this.scaledValue(minMax.min) * config.xAxisBounds.delta +
            config.xAxisBounds.min
        );
    }

    /**
     * Get a range rect's width from a minMax map.
     *
     * @param minMax
     */
    private static getRectWidth(minMax: MinMaxEntry): number {
        return (minMax.delta / 6) * config.xAxisBounds.delta;
    }
    private static rangeValueFromRectValue(rectValue: number): number {
        return this.inverseScaledValue(rectValue / config.xAxisBounds.max);
    }

    /**
     * Get a minMax map from a range-rect's start and end positions.
     *
     * @param startX
     * @param endX
     */
    static getRangeFromRect(startX: number, endX: number): MinMaxEntry {
        return new MinMaxEntry({
            min: this.rangeValueFromRectValue(startX),
            max: this.rangeValueFromRectValue(endX)
        });
    }

    /**
     * Get the start and end position data for the range rects.
     *
     * @param factor
     */
    private static getRectStartEnd(
        draggableRanges: boolean,
        dragMap: DragStartEndMap,
        factor: BehavioralFactor,
        ranges: BehavioralFactorRanges
    ): { start: number; end: number } {
        let dragMapEntry = dragMap[factor];

        if (!draggableRanges) {
            const minMax = ranges[factor];
            const rectWidth = this.getRectWidth(minMax);
            dragMapEntry = {
                start: this.getRectX(minMax),
                end: this.getRectX(minMax) + rectWidth
            };
        }

        return dragMapEntry;
    }

    /**
     * Create the initial drag rect data based on the input factor ranges.
     *
     * @param ranges
     */
    static getInitialDragMap(
        factors: FactorEntry[],
        ranges: BehavioralFactorRanges
    ): DragStartEndMap {
        const dragMap: DragStartEndMap = {} as DragStartEndMap;

        factors.forEach((factor) => {
            const factorKey = factor.key;
            const minMax = ranges[factorKey];
            const rectWidth = this.getRectWidth(minMax);
            dragMap[factorKey] = {
                start: this.getRectX(minMax),
                end: this.getRectX(minMax) + rectWidth
            };
        });

        return dragMap;
    }

    /**
     * Given an x value coordinate from the svg, returns that value bounded
     * by the left and right bounds of the SVG.
     *
     * @param x
     */
    static getBoundedValue(
        dragMap: DragStartEndMap,
        dragFactor: BehavioralFactor,
        draggingRight: boolean,
        x: number
    ): number {
        if (x < 0) {
            return 0;
        }
        if (x > config.xAxisBounds.max) {
            return config.xAxisBounds.max;
        }
        // Check if they're making the range too small
        const dragMapEntry = dragMap[dragFactor];
        const otherRangeValue = draggingRight
            ? dragMapEntry.start
            : dragMapEntry.end;

        const minRangeLength = config.xAxisBounds.max * (0.4 / 6);
        const rangeLength = Math.abs(otherRangeValue - x);
        // Handle the cases where they cross over the other range value
        if (
            (draggingRight && x < otherRangeValue) ||
            (!draggingRight && x > otherRangeValue) ||
            minRangeLength - rangeLength > 0
        ) {
            return otherRangeValue + (draggingRight ? 1 : -1) * minRangeLength;
        }

        return x;
    }

    /**
     * Get the draggable rect's x position for a given factor.
     *
     * @param factor
     * @param isRight The left or right side of the rectangle.
     */
    static getDraggedRectX(
        draggableRanges: boolean,
        dragMap: DragStartEndMap,
        ranges: BehavioralFactorRanges,
        factor: BehavioralFactor,
        isRight: boolean
    ): number {
        const dragMapEntry = this.getRectStartEnd(
            draggableRanges,
            dragMap,
            factor,
            ranges
        );

        return isRight ? dragMapEntry.end : dragMapEntry.start;
    }

    /**
     * Get all the relevant data to draw the factor ranges.
     */
    static getFactorRangesDimensionsData(
        factors: FactorEntry[],
        dragMap: DragStartEndMap,
        draggableRanges: boolean,
        ranges: BehavioralFactorRanges,
        draggableOnMousedown: DraggableOnMousedown = () => () => {
            // Default function is an empty implementation
        }
    ) {
        return factors.map((factor) => {
            const rectStartEnd = this.getRectStartEnd(
                draggableRanges,
                dragMap,
                factor.key,
                ranges
            );
            return {
                rectStartEnd,
                minMax: ranges[factor.key] as MinMax,
                rectY: factor.height - config.rectOffset,
                rectWidth: rectStartEnd.end - rectStartEnd.start,
                factor: factor.key,
                leftRectMousedown: draggableOnMousedown(factor.key, false),
                rightRectMousedown: draggableOnMousedown(factor.key, true)
            };
        });
    }

    /**
     * Get all the relevant data to display the lines for hovered patterns.
     *
     * E.g. the lines that display the ranges for a given pattern overlayed
     * on top of the range rects.
     */
    static getHoveredPatternRangeLinesData(
        hoveredPatternRanges: BehavioralFactorRanges,
        factors: FactorEntry[]
    ) {
        if (!hoveredPatternRanges) {
            return [];
        }
        return factors.map((factor) => {
            const minMax = hoveredPatternRanges[factor.key];
            const x = this.getRectX(minMax);
            const y = factor.height + config.rectHeight / 2 - config.rectOffset;
            const width = this.getRectWidth(minMax);
            const lineWidth = width / 3;

            return {
                minMax,
                x,
                y,
                width,
                lineWidth,
                factorKey: factor.key
            };
        });
    }
}
